import { Button, Modal, toast } from '@insight-health-ai/roseate/components';
import { Checkbox, Form, Input, Select } from 'antd';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { PhoneInput, ScreenLoader } from 'src/components';
import { useDemoStore } from './store';
import styled from '@emotion/styled';
import { useGetSpecialityList } from 'src/hooks/speciality';
import { useCreateDemoCall, useGetCallDetails } from 'src/hooks/call';
import { VALID_PHONE_REGEX } from 'src/utils';
import classNames from 'classnames';
import { useAuth } from '@frontegg/react';
import { analytics } from 'src/services';

// Declare custom element for TypeScript
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'phone-demo': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      >;
    }
  }
}

const html = fetch('/assets/dynamic-html/phone-agent-demo.html').then((res) => {
  return res.text();
});

if (!customElements.get('phone-demo')) {
  customElements.define(
    'phone-demo',
    class extends HTMLElement {
      connectedCallback() {
        this.attachShadow({ mode: 'open' });
        html.then((data) => {
          if (this.shadowRoot) {
            this.shadowRoot.innerHTML = data;
          }
        });
        if (this.shadowRoot) {
          this.shadowRoot.innerHTML = `<div>Loading...</div>`;
        }
      }
    }
  );
}

const scenarios = [
  {
    icon: '📅',
    label: 'Appointment Scheduling',
    value: 'scheduling',
  },

  {
    icon: '💊',
    label: 'Medication Refills',
    value: 'refills',
  },
  {
    icon: '👤',
    label: 'New Patient Intake',
    value: 'new-patient',
  },
  {
    icon: '💳',
    label: 'Billing & Payments',
    value: 'billing',
  },
];

const StyledModal = styled(Modal)`
  .ant-modal-content {
    height: calc(100vh - 64px);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding: 0;
  }
  .ant-modal-body {
    height: 100%;
    overflow: auto;
    padding: 20px 24px;
  }
`;

const inProgressStatuses = ['registered', 'inprogress', 'ongoing'];

export const PhoneDemo = () => {
  const { user } = useAuth();
  const [requestFollowup, setRequestFollowup] = useState<boolean>(true);
  const [callInProgress, setCallInProgress] = useState<boolean>(false);
  const [form] = Form.useForm();
  const selectedScenario = Form.useWatch(['scenario'], form);
  const [isLoading, setIsLoading] = useState(true);
  const { setDemoStoreState, showPhoneTriageDemo } = useDemoStore();

  // const { data: callDetails } = useGetCallDetails(
  //   {
  //     callId: callId ?? '',
  //   },
  //   {
  //     enabled: !!callId,
  //     refetchInterval: (query) =>
  //       inProgressStatuses.includes(
  //         query.state.data?.status?.call_status_key ?? ''
  //       )
  //         ? 3000
  //         : false,
  //   }
  // );

  const { mutateAsync: createDemoCall } = useCreateDemoCall();

  const { data: specialityList } = useGetSpecialityList();

  const specialityOptions = useMemo(() => {
    return specialityList?.specialties.map((speciality) => ({
      label: speciality.specialty_name,
      value: speciality.specialty_id,
    }));
  }, [specialityList]);

  const handleSubmit = async () => {
    const { name, specialty, phone, scenario } = await form.validateFields();

    const payload = {
      to_number: '+1' + phone.replace(/\D/g, ''),
      user_name: name,
      user_email: user?.email ?? '',
      specialty_name: specialty.label,
      specialty_id: specialty.value,
      scenario: scenario.label,
      request_followup: !!requestFollowup,
    };

    analytics.track('Clicked Get Demo Call In Phone Triage Demo', payload);

    try {
      await createDemoCall(payload);
      setCallInProgress(true);
      toast.success(
        'Demo call initiated successfully. You will receive a call shortly.'
      );
    } catch (error) {
      toast.error('Failed to create demo call. Please try again later.');
    }
  };

  useEffect(() => {
    if (showPhoneTriageDemo) {
      analytics.track('Viewed Phone Triage Demo', { email: user?.email });
    }
  }, [showPhoneTriageDemo]);

  useLayoutEffect(() => {
    html
      .then(() => {
        return new Promise((resolve) => setTimeout(resolve, 300));
      })
      .then(() => {
        setIsLoading(false);
      });
  }, []);

  const renderChildren = () => {
    if (callInProgress) {
      return (
        <ScreenLoader
          loaderText="Call connected. You may choose to close this window."
          loaderProps={{ size: 'lg' }}
        />
      );
    }
    if (isLoading) {
      return <ScreenLoader />;
    }

    return (
      <phone-demo>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            name: user?.name,
          }}
        >
          <Form.Item
            className="field-wrapper !mb-7"
            name="name"
            label={<div className="label">Name</div>}
            rules={[
              {
                validator: (_, value, callback) => {
                  if (!value) {
                    callback('Name is required');
                  }
                  callback();
                },
              },
            ]}
          >
            <Input className="field" placeholder="Full Name" />
          </Form.Item>
          <Form.Item
            className="field-wrapper !mb-7"
            name="specialty"
            label={<div className="label">Specialty</div>}
            rules={[
              {
                validator: (_, value, callback) => {
                  if (!value) {
                    callback('Specialty is required');
                  }
                  callback();
                },
              },
            ]}
          >
            <Select
              placeholder="Select specialty"
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              labelInValue
              className="field"
              options={specialityOptions}
            />
          </Form.Item>
          <Form.Item
            className="field-wrapper !mb-7"
            name="phone"
            label={<div className="label">Phone</div>}
            rules={[
              {
                validator: (_, value, callback) => {
                  if (!value) {
                    callback('Phone is required');
                  }
                  callback();
                },
              },
              {
                pattern: VALID_PHONE_REGEX,
                message: 'Invalid phone number',
              },
            ]}
          >
            <PhoneInput className="field" />
          </Form.Item>
          <Form.Item
            className="field-wrapper !mb-7"
            name="scenario"
            label={
              <div className="label">Select a scenario to experience:</div>
            }
            rules={[
              {
                validator: (_, value, callback) => {
                  if (!value) {
                    callback('Please pick a scenario');
                  }
                  callback();
                },
              },
            ]}
          >
            <div className="grid grid-cols-2 gap-4">
              {scenarios.map((scenario) => (
                <div
                  key={scenario.value}
                  className={classNames(
                    'flex flex-col justify-center items-center gap-2 px-4 py-8 border border-gray-200 rounded-lg cursor-pointer',
                    {
                      'bg-primary text-white border-primary scale-105':
                        scenario.value === selectedScenario?.value,
                      'bg-primary/5 border-primary/30':
                        scenario.value !== selectedScenario?.value,
                    },
                    'hover:bg-primary hover:text-white hover:border-primary hover:scale-105 transition-all duration-300'
                  )}
                  onClick={() => form.setFieldValue('scenario', scenario)}
                >
                  <div className="text-2xl">{scenario.icon}</div>
                  <div className="font-medium">{scenario.label}</div>
                </div>
              ))}
            </div>
          </Form.Item>
        </Form>
        <div className="flex flex-col gap-4 mt-10">
          <div className="flex flex-col gap-2">
            <Button className="!h-12" size="lg" block onClick={handleSubmit}>
              Get a Demo Call Now
            </Button>
            <Checkbox
              checked={requestFollowup}
              onChange={(e) => {
                setRequestFollowup(e.target.checked);
                analytics.track(
                  'Changed Request Followup Checkbox In Phone Triage Demo',
                  { email: user?.email, request_followup: e.target.checked }
                );
              }}
            >
              <div className="text-sm text-gray-500">
                I'm interested in learning more and like to be contacted for a
                personalized demonstration.
              </div>
            </Checkbox>
          </div>
        </div>
      </phone-demo>
    );
  };

  return (
    <StyledModal
      footer={() => null}
      className="demo-modal"
      width={1300}
      open={showPhoneTriageDemo}
      onClose={() => {
        setDemoStoreState({ showPhoneTriageDemo: false });
      }}
    >
      {renderChildren()}
    </StyledModal>
  );
};
